import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { nextSeat, ringStep, spreadSides } from '@campfire/track';

/** The ring has 68 squares: four arms of 17. Arm q starts at ring square 17q. */
export const ARM = 17;
export const RING = 68;
/**
 * A pawn's progress: 0 = in the nest, 1..64 = ring squares from its entry square,
 * 65..71 = its home column, 72 = home.
 */
export const NEST = 0;
export const RING_END = 64;
export const HOME = 72;
export const BONUS_CAPTURE = 20;
export const BONUS_HOME = 10;
export const PAWNS = 4;

/** Entry square for the arm: the 5th square of the arm. */
export const entrySquare = (arm: number): number => arm * ARM + 4;
/** Safe squares: the 5th, 12th and 17th square of every arm (the 5th is where that colour enters). */
export const SAFE_SQUARES: readonly number[] = Array.from({ length: 4 }, (_, a) => [a * ARM + 4, a * ARM + 11, a * ARM + 16]).flat();
export const isSafe = (sq: number): boolean => SAFE_SQUARES.includes(sq);
/** Ring square for progress 1..64 of a pawn from `arm`. */
export const ringSquare = (arm: number, progress: number): number => ringStep(entrySquare(arm), progress - 1, RING);

export interface ParcheesiOptions {
  /** With every pawn out of the nest, doubles also give the opposite faces of the dice. */
  readonly oppositeFaces: boolean;
}

/** One piece of the roll still to be played: a die, or a bonus earned by a capture or reaching home. */
export interface Pip {
  readonly kind: 'die' | 'bonus';
  readonly value: number;
}

export type ParcheesiAction =
  | { readonly type: 'roll' }
  /** Enter a pawn using the pips at these indexes (a single 5, or two dice adding to 5). */
  | { readonly type: 'enter'; readonly use: readonly number[] }
  | { readonly type: 'move'; readonly pawn: number; readonly use: number };

export interface ParcheesiEvent {
  readonly seat: Seat;
  readonly kind: 'rolled' | 'entered' | 'moved' | 'forfeit' | 'penalty';
  readonly dice: readonly [number, number] | null;
  readonly doubles: boolean;
  readonly pawn: number | null;
  readonly from: number;
  readonly to: number;
  readonly captured: { readonly seat: Seat; readonly pawn: number } | null;
  readonly bonus: number;
  /** Pawn sent back to the nest by the third doubles. */
  readonly sentBack: number | null;
}

export interface ParcheesiState {
  readonly options: ParcheesiOptions;
  readonly arms: readonly number[];
  readonly pawns: readonly (readonly number[])[];
  readonly turn: Seat;
  readonly phase: 'roll' | 'move';
  readonly dice: readonly [number, number] | null;
  readonly remaining: readonly Pip[];
  /** Doubles rolled so far in this run of turns. */
  readonly doublesRun: number;
  /** The current roll was doubles, so the seat rolls again once it has been played. */
  readonly rollAgain: boolean;
  readonly last: ParcheesiEvent | null;
  readonly result: Outcome | null;
}

export interface ParcheesiMove {
  readonly action: ParcheesiAction;
  readonly pawn: number;
  readonly from: number;
  readonly to: number;
  readonly captures: boolean;
}

export interface ParcheesiView {
  readonly you: Seat | null;
  readonly arms: readonly number[];
  readonly pawns: readonly (readonly number[])[];
  readonly turn: Seat;
  readonly phase: 'roll' | 'move';
  readonly dice: readonly [number, number] | null;
  readonly remaining: readonly Pip[];
  readonly doublesRun: number;
  readonly last: ParcheesiEvent | null;
  readonly result: Outcome | null;
  readonly oppositeFaces: boolean;
  readonly legal: readonly ParcheesiMove[];
}

type Pawns = readonly (readonly number[])[];
type Cand = ParcheesiMove;

/** Pawns standing on a ring square: [seat, pawn] pairs. */
function occupants(pawns: Pawns, arms: readonly number[], sq: number): [Seat, number][] {
  const out: [Seat, number][] = [];
  pawns.forEach((row, s) =>
    row.forEach((p, i) => {
      if (p >= 1 && p <= RING_END && ringSquare(arms[s] as number, p) === sq) out.push([s, i]);
    }),
  );
  return out;
}
/** A blockade is two pawns of one colour on a ring square. */
export function isBlockade(pawns: Pawns, arms: readonly number[], sq: number): boolean {
  const o = occupants(pawns, arms, sq);
  return o.length >= 2 && (o[0] as [Seat, number])[0] === (o[1] as [Seat, number])[0];
}

/** What happens if `seat`'s pawn goes to progress `to` (from `from`; 0 = entering). Null when it cannot. */
function landing(pawns: Pawns, arms: readonly number[], seat: Seat, pawn: number, from: number, to: number): { captured: [Seat, number] | null } | null {
  if (to > HOME) return null;
  const arm = arms[seat] as number;
  // Cannot pass through or land on a blockade (anyone's, including your own).
  for (let p = from + 1; p <= Math.min(to, RING_END); p++) {
    if (isBlockade(pawns, arms, ringSquare(arm, p))) return null;
  }
  if (to > RING_END) return { captured: null };
  const sq = ringSquare(arm, to);
  const foe = occupants(pawns, arms, sq).find(([s, i]) => s !== seat && !(s === seat && i === pawn));
  if (!foe) return { captured: null };
  // Only a pawn entering from the nest may take a pawn off a safe square.
  if (isSafe(sq) && from !== NEST) return null;
  return { captured: foe };
}

interface Applied {
  readonly pawns: Pawns;
  readonly remaining: readonly Pip[];
  readonly captured: { seat: Seat; pawn: number } | null;
  readonly bonus: number;
}

function playCand(pawns: Pawns, arms: readonly number[], remaining: readonly Pip[], seat: Seat, c: Cand, award: boolean): Applied {
  const use = c.action.type === 'enter' ? c.action.use : c.action.type === 'move' ? [c.action.use] : [];
  const l = landing(pawns, arms, seat, c.pawn, c.from, c.to) as { captured: [Seat, number] | null };
  const next = pawns.map((row, s) =>
    row.map((p, i) => {
      if (s === seat && i === c.pawn) return c.to;
      if (l.captured && s === l.captured[0] && i === l.captured[1]) return NEST;
      return p;
    }),
  );
  const gained: Pip[] = [];
  if (award && l.captured) gained.push({ kind: 'bonus', value: BONUS_CAPTURE });
  if (award && c.to === HOME) gained.push({ kind: 'bonus', value: BONUS_HOME });
  return {
    pawns: next,
    remaining: [...remaining.filter((_, i) => !use.includes(i)), ...gained],
    captured: l.captured ? { seat: l.captured[0], pawn: l.captured[1] } : null,
    bonus: gained.reduce((n, g) => n + g.value, 0),
  };
}

/** All plays for the pips in `remaining`, before the "use as many as you can" rule. */
function rawCandidates(pawns: Pawns, arms: readonly number[], remaining: readonly Pip[], seat: Seat): Cand[] {
  const out: Cand[] = [];
  const mine = pawns[seat] as readonly number[];
  const seenPip = new Set<string>();
  remaining.forEach((pip, idx) => {
    const key = `${pip.kind}${pip.value}`;
    if (seenPip.has(key)) return;
    seenPip.add(key);
    const seenPos = new Set<number>();
    mine.forEach((p, pawn) => {
      if (p === NEST || p === HOME || seenPos.has(p)) return;
      seenPos.add(p);
      const l = landing(pawns, arms, seat, pawn, p, p + pip.value);
      if (l) out.push({ action: { type: 'move', pawn, use: idx }, pawn, from: p, to: p + pip.value, captures: l.captured !== null });
    });
  });
  const nestPawn = mine.indexOf(NEST);
  if (nestPawn >= 0) {
    const l = landing(pawns, arms, seat, nestPawn, NEST, 1);
    if (l) {
      const enter = (use: number[]) => out.push({ action: { type: 'enter', use }, pawn: nestPawn, from: NEST, to: 1, captures: l.captured !== null });
      const five = remaining.findIndex((p) => p.kind === 'die' && p.value === 5);
      if (five >= 0) enter([five]);
      const seenPair = new Set<number>();
      for (let i = 0; i < remaining.length; i++) {
        for (let j = i + 1; j < remaining.length; j++) {
          const a = remaining[i] as Pip;
          const b = remaining[j] as Pip;
          if (a.kind === 'die' && b.kind === 'die' && a.value + b.value === 5 && !seenPair.has(a.value)) {
            seenPair.add(a.value);
            seenPair.add(b.value);
            enter([i, j]);
          }
        }
      }
    }
  }
  return out;
}

/** Most pips that can still be played from here (no new bonuses are counted). */
function maxUse(pawns: Pawns, arms: readonly number[], remaining: readonly Pip[], seat: Seat, memo: Map<string, number>): number {
  if (remaining.length === 0) return 0;
  const key = JSON.stringify([pawns, remaining]);
  const hit = memo.get(key);
  if (hit !== undefined) return hit;
  let best = 0;
  for (const c of rawCandidates(pawns, arms, remaining, seat)) {
    const a = playCand(pawns, arms, remaining, seat, c, false);
    best = Math.max(best, remaining.length - a.remaining.length + maxUse(a.pawns, arms, a.remaining, seat, memo));
  }
  memo.set(key, best);
  return best;
}

/** Legal plays: any play after which as many pips as possible can still be used. */
function candidates(s: ParcheesiState): Cand[] {
  if (s.phase !== 'move' || s.result) return [];
  const seat = s.turn;
  const raw = rawCandidates(s.pawns, s.arms, s.remaining, seat);
  if (raw.length === 0) return [];
  const memo = new Map<string, number>();
  const best = maxUse(s.pawns, s.arms, s.remaining, seat, memo);
  return raw.filter((c) => {
    const a = playCand(s.pawns, s.arms, s.remaining, seat, c, false);
    return s.remaining.length - a.remaining.length + maxUse(a.pawns, s.arms, a.remaining, seat, memo) === best;
  });
}

const sameAction = (a: ParcheesiAction, b: ParcheesiAction): boolean => JSON.stringify(a) === JSON.stringify(b);

const event = (seat: Seat, kind: ParcheesiEvent['kind'], over: Partial<ParcheesiEvent> = {}): ParcheesiEvent => ({
  seat, kind, dice: null, doubles: false, pawn: null, from: 0, to: 0, captured: null, bonus: 0, sentBack: null, ...over,
});

/** The next player's turn (or the same player after doubles), at the roll step. */
function endOfPlay(s: ParcheesiState, last: ParcheesiEvent): ParcheesiState {
  const again = s.rollAgain;
  return {
    ...s, remaining: [], dice: null, phase: 'roll', rollAgain: false, last,
    turn: again ? s.turn : nextSeat(s.turn, s.pawns.length),
    doublesRun: again ? s.doublesRun : 0,
  };
}

/** Ends the game on a win; forfeits whatever cannot be played. */
function settle(s: ParcheesiState, last: ParcheesiEvent): ParcheesiState {
  const winner = s.pawns.findIndex((row) => row.every((p) => p === HOME));
  if (winner >= 0) return { ...s, remaining: [], phase: 'roll', last, result: { winners: [winner], draw: false, reason: `${winner} got every pawn home` } };
  if (s.remaining.length === 0) return endOfPlay(s, last);
  if (candidates(s).length === 0) return endOfPlay(s, last.kind === 'rolled' ? { ...last, kind: 'forfeit' } : last);
  return { ...s, last };
}

/** The legal plays for the player to act, with what each would do. */
export function legalMoves(s: ParcheesiState): ParcheesiMove[] {
  return candidates(s);
}

export const parcheesi: GameDefinition<ParcheesiState, ParcheesiAction, ParcheesiView, ParcheesiOptions> = {
  id: 'parcheesi',
  name: 'Parcheesi',
  rules: [
    'Bring all four of your pawns around the board and into the centre home. Everyone starts with their pawns in the nest.',
    'On your turn roll both dice. Play each die as a separate move with any pawn, in either order. You must play both dice if you can.',
    'A 5 on one die, or two dice that add up to 5, brings a pawn out of the nest onto your entry square. You cannot enter if a blockade is on it.',
    'Safe squares are marked. A pawn on a safe square cannot be captured, and you cannot land on a safe square that holds an opponent. The exception is your entry square: entering there captures an opponent sitting on it.',
    'Landing on a single opponent pawn on an ordinary square captures it: it goes back to its nest and you get a bonus move of 20 with any one pawn.',
    'A pawn that reaches home exactly earns a bonus move of 10 with any one pawn. You need the exact number to get home. Bonuses are lost if no pawn can use them.',
    'Two of your pawns on one square make a blockade. Nobody, including you, can land on or pass a blockade.',
    'Doubles: after you play the roll, roll again. A third doubles in a row is not played: your pawn closest to home goes back to the nest and your turn ends.',
    'Setting: with every pawn out of the nest, doubles also give the opposite faces of the dice (7 minus each die) as two more moves. Turn it off to play without it.',
    'Not enforced: the table may agree that a blockade cannot be held for more than three turns, and that a blockade cannot be built on an entry square. The app also does not make you use the larger die when only one die can be played.',
  ],
  minSeats: 2,
  maxSeats: 4,
  defaultOptions: { oppositeFaces: true },
  optionSpecs: [
    {
      key: 'oppositeFaces',
      label: 'Doubles use opposite faces',
      help: 'Once all your pawns are out of the nest, doubles also give the bottom faces of the dice.',
      choices: [
        { value: true, label: 'On' },
        { value: false, label: 'Off' },
      ],
    },
  ],

  setup: ({ seats, options }) => ({
    options,
    arms: spreadSides(seats),
    pawns: Array.from({ length: seats }, () => Array.from({ length: PAWNS }, () => NEST)),
    turn: 0,
    phase: 'roll',
    dice: null,
    remaining: [],
    doublesRun: 0,
    rollAgain: false,
    last: null,
    result: null,
  }),

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn || s.result) return { ok: false, reason: 'not your turn' };
    if (a.type === 'roll') return s.phase === 'roll' ? { ok: true } : { ok: false, reason: 'play your dice first' };
    if (s.phase !== 'move') return { ok: false, reason: 'roll first' };
    return candidates(s).some((c) => sameAction(c.action, a)) ? { ok: true } : { ok: false, reason: 'illegal move' };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    if (a.type === 'roll') {
      const dice: [number, number] = [rng.int(6) + 1, rng.int(6) + 1];
      const doubles = dice[0] === dice[1];
      const run = s.doublesRun + (doubles ? 1 : 0);
      const rolled = event(seat, 'rolled', { dice, doubles });
      if (run >= 3) {
        const row = s.pawns[seat] as readonly number[];
        let far = -1;
        row.forEach((p, i) => {
          if (p > 0 && p < HOME && (far < 0 || p > (row[far] as number))) far = i;
        });
        const pawns = s.pawns.map((r, i) => (i === seat && far >= 0 ? r.map((p, j) => (j === far ? NEST : p)) : r));
        return endOfPlay({ ...s, pawns, rollAgain: false }, { ...rolled, kind: 'penalty', sentBack: far >= 0 ? far : null });
      }
      const pips: Pip[] = dice.map((value) => ({ kind: 'die', value }));
      if (doubles && s.options.oppositeFaces && (s.pawns[seat] as readonly number[]).every((p) => p !== NEST)) {
        pips.push({ kind: 'die', value: 7 - dice[0] }, { kind: 'die', value: 7 - dice[1] });
      }
      return settle({ ...s, phase: 'move', dice, remaining: pips, doublesRun: run, rollAgain: doubles }, rolled);
    }
    const c = candidates(s).find((x) => sameAction(x.action, a)) as Cand;
    const r = playCand(s.pawns, s.arms, s.remaining, seat, c, true);
    const ev = event(seat, a.type === 'enter' ? 'entered' : 'moved', {
      dice: s.dice, doubles: s.rollAgain, pawn: c.pawn, from: c.from, to: c.to, captured: r.captured, bonus: r.bonus,
    });
    return settle({ ...s, pawns: r.pawns, remaining: r.remaining }, ev);
  },

  view(s, viewer: Viewer): ParcheesiView {
    const mover = viewer.kind === 'seat' && viewer.seat === s.turn;
    return {
      you: viewer.kind === 'seat' ? viewer.seat : null,
      arms: s.arms,
      pawns: s.pawns,
      turn: s.turn,
      phase: s.phase,
      dice: s.dice,
      remaining: s.remaining,
      doublesRun: s.doublesRun,
      last: s.last,
      result: s.result,
      oppositeFaces: s.options.oppositeFaces,
      legal: mover ? candidates(s) : [],
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (seat !== s.turn || s.result) return [];
    return s.phase === 'roll' ? [{ type: 'roll' }] : candidates(s).map((c) => c.action);
  },
};
